import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, Search, RefreshCw } from 'lucide-react';
import Loading from './Loading';
import ErrorMessage from './ErrorMessage';
import {
  computeAiOpportunitySummary,
  computeHighImpactOpportunities,
  computePageAiRecommendation,
  estimateBulkTimeSeconds,
  estimateSelectedScoreGain,
} from '../utils/seoOptimizerRecommendations';
import './seo-optimizer-action-first.css';

function getScoreColorClass(score) {
  if (score > 95) return 'text-success';
  if (score >= 80) return 'text-warning';
  return 'text-danger';
}

function impactClass(impact) {
  if (impact === 'High') return 'ss-seo-impact ss-seo-impact--high';
  return 'ss-seo-impact ss-seo-impact--medium';
}

export default function SEOOptimizerOverview({
  pages,
  paginatedPages,
  filteredPages,
  totalErrors,
  avgScore,
  stats,
  cacheMetadata,
  hasCachedData,
  isLoading,
  isLoadingStats,
  isRunningAudit,
  isValidating,
  error,
  selectedItems,
  onSelectAll,
  onSelectItem,
  onFixWithAi,
  onOptimizeSelected,
  onOpportunityFix,
  onRunAudit,
  onValidateOutdated,
  onRefresh,
  onAuditAllProducts,
  onUpgradeToAuditAllProducts,
  isAuditingAllProducts,
  opportunityFilter,
  onClearOpportunityFilter,
  filterType,
  setFilterType,
  productListingFilter,
  setProductListingFilter,
  sortBy,
  setSortBy,
  scoreFilter,
  setScoreFilter,
  issueFilter,
  setIssueFilter,
  searchQuery,
  setSearchQuery,
  setCurrentPage,
  currentPage,
  itemsPerPage,
  setItemsPerPage,
  totalPages,
  areFiltersActive,
}) {
  const { t } = useTranslation();
  const tableRef = useRef(null);

  const summary = useMemo(
    () => computeAiOpportunitySummary(pages, totalErrors, Math.round(avgScore)),
    [pages, totalErrors, avgScore]
  );
  const opportunities = useMemo(() => computeHighImpactOpportunities(pages), [pages]);
  const selectedGain = useMemo(
    () => estimateSelectedScoreGain(pages, selectedItems),
    [pages, selectedItems]
  );
  const selectedTime = estimateBulkTimeSeconds(selectedItems.size);

  const pagesUnaudited = Math.max(0, stats.pagesAudited.total - stats.pagesAudited.current);
  const productsUnaudited = Math.max(0, stats.products.total - stats.products.current);

  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!hasCachedData && !isLoading) {
    return (
      <div className="ss-seo-opt">
        <section className="ss-seo-hero card border-0 shadow-sm mb-4">
          <div className="card-body p-4 p-md-5 text-center">
            <div className="ss-seo-hero__eyebrow">{t('seoOptimizerOverview.eyebrow')}</div>
            <h2 className="ss-seo-hero__title mb-2">{t('seoOptimizerOverview.emptyTitle')}</h2>
            <p className="text-muted mb-4" style={{ maxWidth: '32rem', margin: '0 auto' }}>
              {t('seoOptimizerOverview.emptyDesc')}
            </p>
            <button type="button" className="btn btn-primary btn-lg fw-semibold" onClick={onRunAudit}>
              <Rocket size={18} className="me-2" />
              {t('seoOptimizerOverview.startAudit')}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="ss-seo-opt">
      <section className="ss-seo-hero card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="row g-4 align-items-center">
            <div className="col-12 col-lg-7">
              <div className="ss-seo-hero__eyebrow">{t('seoOptimizerOverview.eyebrow')}</div>
              <h2 className="ss-seo-hero__title">
                {isLoadingStats
                  ? t('seoOptimizerOverview.analyzing')
                  : t('seoOptimizerOverview.issuesFound', {
                      count: summary.totalIssues,
                      defaultValue: `We found ${summary.totalIssues.toLocaleString()} SEO issue${summary.totalIssues === 1 ? '' : 's'} across your audited products.`,
                    })}
              </h2>
              {!isLoadingStats && summary.fixableByAi > 0 ? (
                <>
                  <p className="mb-2 fw-semibold" style={{ color: '#374151' }}>
                    {t('seoOptimizerOverview.aiCanImprove')}
                  </p>
                  <ul className="ss-seo-hero__bullets">
                    {summary.metaIssues > 0 ? (
                      <li>{t('seoOptimizerOverview.metaIssues', { count: summary.metaIssues, defaultValue: `${summary.metaIssues} metadata issue${summary.metaIssues === 1 ? '' : 's'}` })}</li>
                    ) : null}
                    {summary.contentIssues > 0 ? (
                      <li>{t('seoOptimizerOverview.contentIssues', { count: summary.contentIssues, defaultValue: `${summary.contentIssues} content issue${summary.contentIssues === 1 ? '' : 's'}` })}</li>
                    ) : null}
                    {summary.titleIssues > 0 ? (
                      <li>{t('seoOptimizerOverview.titleIssues', { count: summary.titleIssues, defaultValue: `${summary.titleIssues} title issue${summary.titleIssues === 1 ? '' : 's'}` })}</li>
                    ) : null}
                    {summary.imageIssues > 0 ? (
                      <li>{t('seoOptimizerOverview.imageIssues', { count: summary.imageIssues, defaultValue: `${summary.imageIssues} image SEO issue${summary.imageIssues === 1 ? '' : 's'}` })}</li>
                    ) : null}
                  </ul>
                </>
              ) : (
                <p className="text-muted mb-0">{t('seoOptimizerOverview.healthyPages')}</p>
              )}
            </div>
            <div className="col-12 col-lg-5">
              <div className="border rounded-3 bg-white p-3 p-md-4">
                <div className="text-muted small fw-semibold mb-2">
                  {t('seoOptimizerOverview.estimatedImprovement')}
                </div>
                <div className="ss-seo-score-pair mb-3">
                  <span>{summary.currentScore}</span>
                  <span className="ss-seo-score-pair__arrow" aria-hidden>
                    →
                  </span>
                  <span style={{ color: '#7c3aed' }}>{summary.projectedScore}</span>
                </div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <span className="ss-seo-meta-pill">
                    {t('seoOptimizerOverview.timeEstimate', { time: summary.timeEstimate })}
                  </span>
                  {stats.pagesAudited.current < stats.pagesAudited.total ? (
                    <span className="ss-seo-meta-pill">
                      {t('seoOptimizerOverview.basedOnAudited', {
                        count: stats.pagesAudited.current,
                      })}
                    </span>
                  ) : null}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary fw-semibold"
                    onClick={scrollToTable}
                    disabled={summary.fixableByAi === 0}
                  >
                    {t('seoOptimizerOverview.reviewRecommendations')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary fw-semibold"
                    onClick={onRunAudit}
                    disabled={isRunningAudit || isLoading}
                  >
                    {isRunningAudit
                      ? t('seoOptimizerOverview.runningAudit')
                      : t('seoOptimizerOverview.rerunSeoAudit')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {opportunities.length > 0 ? (
        <section className="mb-4">
          <h6 className="fw-bold mb-1" style={{ color: '#1f2937' }}>
            {t('seoOptimizerOverview.highestImpact')}
          </h6>
          <p className="text-muted small mb-3">{t('seoOptimizerOverview.highestImpactDesc')}</p>
          <div className="row g-3">
            {opportunities.map((opp) => (
              <div key={opp.id} className="col-12 col-md-6 col-xl-3">
                <div className="ss-seo-opportunity-card">
                  <div className="ss-seo-opportunity-card__title">{opp.label}</div>
                  <div className="text-muted small mb-2">
                    {t('seoOptimizerOverview.productsAffected', {
                      count: opp.affectedCount,
                      defaultValue: `${opp.affectedCount} product${opp.affectedCount === 1 ? '' : 's'} affected`,
                    })}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <span className={impactClass(opp.impact)}>
                      {t('seoOptimizerOverview.scoreGain', { points: opp.scoreGain })}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary fw-semibold w-100"
                    onClick={() => {
                      onOpportunityFix(opp.id);
                      scrollToTable();
                    }}
                  >
                    {t('seoOptimizerOverview.fixWithAi')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {selectedItems.size > 0 ? (
        <section className="ss-seo-queue card border-0 shadow-sm mb-4">
          <div className="card-body p-3 p-md-4">
            <div className="row g-3 align-items-center">
              <div className="col-6 col-md-3">
                <div className="ss-seo-queue__stat">{selectedItems.size}</div>
                <div className="ss-seo-queue__label">{t('seoOptimizerOverview.selectedProducts')}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="ss-seo-queue__stat">+{selectedGain}</div>
                <div className="ss-seo-queue__label">{t('seoOptimizerOverview.potentialGain')}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="ss-seo-queue__stat" style={{ fontSize: '1.1rem' }}>
                  {selectedTime}
                </div>
                <div className="ss-seo-queue__label">{t('seoOptimizerOverview.estimatedTime')}</div>
              </div>
              <div className="col-12 col-md-3 text-md-end">
                <button
                  type="button"
                  className="btn btn-primary fw-semibold w-100 w-md-auto"
                  onClick={onOptimizeSelected}
                >
                  {t('seoOptimizerOverview.optimizeSelected')}
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {cacheMetadata.isCached && cacheMetadata.cacheAge ? (
        <div className="ss-seo-validation-banner d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <span>
            {cacheMetadata.outdatedCount > 0
              ? t('seoOptimizerOverview.cacheOutdated', {
                  age: cacheMetadata.cacheAge,
                  count: cacheMetadata.outdatedCount,
                  defaultValue: `SEO audit data is ${cacheMetadata.cacheAge}. ${cacheMetadata.outdatedCount} page${cacheMetadata.outdatedCount === 1 ? '' : 's'} may be outdated.`,
                })
              : t('seoOptimizerOverview.cacheFresh', { age: cacheMetadata.cacheAge })}
          </span>
          {onValidateOutdated && cacheMetadata.outdatedCount > 0 ? (
            <button
              type="button"
              className="btn btn-sm btn-warning fw-semibold"
              onClick={onValidateOutdated}
              disabled={isValidating}
            >
              {isValidating
                ? t('seoOptimizerOverview.refreshing')
                : t('seoOptimizerOverview.refreshAudit')}
            </button>
          ) : null}
        </div>
      ) : null}

      <section ref={tableRef} className="ss-seo-table-section mb-4">
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h6 className="fw-semibold mb-0">{t('seoOptimizerOverview.productRecommendations')}</h6>
                <small className="text-muted">
                  {t('seoOptimizerOverview.tableSubtitle')}
                  {opportunityFilter ? (
                    <>
                      {' '}
                      ·{' '}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={onClearOpportunityFilter}
                      >
                        {t('seoOptimizerOverview.clearFilter')}
                      </button>
                    </>
                  ) : null}
                </small>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={onRefresh}
                  disabled={isLoading}
                  title={t('seoOptimizer.refreshTable')}
                >
                  <RefreshCw
                    size={16}
                    style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }}
                  />
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm fw-semibold"
                  onClick={onRunAudit}
                  disabled={isRunningAudit || isLoading}
                >
                  {isRunningAudit
                    ? t('seoOptimizerOverview.runningAudit')
                    : areFiltersActive && filteredPages.length > 0
                      ? t('seoOptimizerOverview.runAuditWithCount', { count: filteredPages.length })
                      : t('seoOptimizerOverview.runAudit')}
                </button>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex gap-2 flex-wrap">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: '120px' }}
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="All">{t('seoOptimizerOverview.filterAll')}</option>
                  <option value="Products">{t('dashboard.products')}</option>
                  <option value="Categories">{t('dashboard.categories')}</option>
                </select>
                {filterType === 'Products' && setProductListingFilter ? (
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', minWidth: '110px' }}
                    value={productListingFilter}
                    onChange={(e) => {
                      setProductListingFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    aria-label={t('seoOptimizer.productListingFilter')}
                  >
                    <option value="all">{t('seoOptimizer.listingAll')}</option>
                    <option value="online">{t('seoOptimizer.listingOnlineOnly')}</option>
                  </select>
                ) : null}
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: '150px' }}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="Name - A to Z">{t('seoOptimizer.nameAZ')}</option>
                  <option value="Name - Z to A">{t('seoOptimizer.nameZA')}</option>
                  <option value="SEO Score - High to Low">{t('seoOptimizer.scoreHighLow')}</option>
                  <option value="SEO Score - Low to High">{t('seoOptimizer.scoreLowHigh')}</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: '140px' }}
                  value={scoreFilter}
                  onChange={(e) => setScoreFilter(e.target.value)}
                >
                  <option value="Any">{t('seoOptimizerOverview.scoreAny')}</option>
                  <option value="96-100">{t('seoOptimizerOverview.score96')}</option>
                  <option value="80-95">{t('seoOptimizerOverview.score80')}</option>
                  <option value="0-79">{t('seoOptimizerOverview.score0')}</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: '150px' }}
                  value={issueFilter}
                  onChange={(e) => setIssueFilter(e.target.value)}
                >
                  <option value="Any">{t('seoOptimizerOverview.errorsAny')}</option>
                  <option value="Critical">{t('seoOptimizerOverview.errorsCritical')}</option>
                  <option value="None">{t('seoOptimizerOverview.errorsNone')}</option>
                </select>
              </div>
              <div className="input-group input-group-sm" style={{ width: '200px' }}>
                <span className="input-group-text bg-white">
                  <Search size={14} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('seoOptimizer.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {isLoading ? (
              <div className="text-center p-5">
                <Loading />
              </div>
            ) : error ? (
              <div className="p-3">
                <ErrorMessage error={error} />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '30px' }}>
                        <input
                          type="checkbox"
                          checked={
                            selectedItems.size === paginatedPages.length && paginatedPages.length > 0
                          }
                          onChange={onSelectAll}
                          aria-label={t('seoOptimizerOverview.selectAll')}
                        />
                      </th>
                      <th>{t('seoOptimizerOverview.colProduct')}</th>
                      <th>{t('seoOptimizerOverview.colScore')}</th>
                      <th>{t('seoOptimizerOverview.colRecommendation')}</th>
                      <th>{t('seoOptimizer.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPages.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-5 text-muted">
                          {t('seoOptimizerOverview.noMatches')}
                          {opportunityFilter ? ` ${t('seoOptimizerOverview.tryClearFilter')}` : ''}
                        </td>
                      </tr>
                    ) : (
                      paginatedPages.map((page) => {
                        const recommendation = computePageAiRecommendation(page);
                        const hasFix = recommendation !== 'No AI fixes needed';
                        return (
                          <tr key={page.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedItems.has(page.id)}
                                onChange={() => onSelectItem(page.id)}
                                aria-label={t('seoOptimizerOverview.selectItem', { name: page.name })}
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {page.image_url ? (
                                  <img
                                    src={page.image_url}
                                    alt=""
                                    style={{
                                      width: '48px',
                                      height: '48px',
                                      objectFit: 'cover',
                                      borderRadius: '0.5rem',
                                      border: '1px solid #e9ecef',
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: '48px',
                                      height: '48px',
                                      background: '#f8f9fa',
                                      border: '1px solid #e9ecef',
                                      borderRadius: '0.5rem',
                                    }}
                                  />
                                )}
                                <div>
                                  <div className="fw-semibold">{page.name}</div>
                                  <div className="text-muted small">{page.type}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`fw-bold ${getScoreColorClass(page.seoScore)}`}>
                                {page.seoScore}%
                              </span>
                            </td>
                            <td className="ss-seo-rec-cell">
                              {hasFix ? recommendation : t('seoOptimizerOverview.noAiFixes')}
                            </td>
                            <td>
                              <button
                                type="button"
                                className={`btn btn-sm fw-semibold ${hasFix ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => onFixWithAi(page)}
                              >
                                {hasFix
                                  ? t('seoOptimizerOverview.fixWithAi')
                                  : t('seoOptimizerOverview.review')}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>{t('seoOptimizerOverview.perPage10')}</option>
                <option value={20}>{t('seoOptimizerOverview.perPage20')}</option>
                <option value={50}>{t('seoOptimizerOverview.perPage50')}</option>
                <option value={100}>{t('seoOptimizerOverview.perPage100')}</option>
              </select>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t('seoOptimizer.previous')}
                </button>
                <span className="text-muted small">
                  {t('seoOptimizer.pageXofY', {
                    current: currentPage,
                    total: Math.max(1, totalPages),
                  })}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                >
                  {t('seoOptimizer.next')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ss-seo-audit-strip mb-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div className="fw-semibold mb-1">{t('seoOptimizerOverview.auditStatus')}</div>
            <div className="text-muted small">
              {t('seoOptimizerOverview.pagesAudited', {
                current: stats.pagesAudited.current.toLocaleString(),
                total: stats.pagesAudited.total.toLocaleString(),
              })}
              {pagesUnaudited > 0
                ? ` · ${t('seoOptimizerOverview.pagesUnaudited', { count: pagesUnaudited, defaultValue: `${pagesUnaudited} page${pagesUnaudited === 1 ? '' : 's'} remain unaudited` })}`
                : ''}
              {productsUnaudited > 0
                ? ` · ${t('seoOptimizerOverview.productsUnaudited', { count: productsUnaudited, defaultValue: `${productsUnaudited} product${productsUnaudited === 1 ? '' : 's'} not audited` })}`
                : ''}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {onUpgradeToAuditAllProducts ? (
              <button
                type="button"
                className="btn btn-sm btn-primary fw-semibold"
                onClick={onUpgradeToAuditAllProducts}
              >
                {t('seoOptimizerOverview.upgradeAuditAll')}
              </button>
            ) : null}
            {onAuditAllProducts && !onUpgradeToAuditAllProducts ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary fw-semibold"
                onClick={onAuditAllProducts}
                disabled={isAuditingAllProducts}
              >
                {isAuditingAllProducts
                  ? t('seoOptimizerOverview.startingAudit')
                  : t('seoOptimizerOverview.completeFullAudit')}
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
